import { useMemo } from 'react';
import { Card } from '../../components';
import type { AppData } from '../../data/types';
import { formatCurrency } from '../../lib/currency';
import { addDays } from '../../lib/date';
import { txContributionInRange } from '../../lib/budget';

type Props = {
  data: AppData;
  today: Date;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dowName(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(y!, (m ?? 1) - 1, d ?? 1).getDay();
  return DAY_NAMES[dow] ?? '—';
}

/**
 * Compact weekly stats card: total spent, daily average, biggest day,
 * top category, vs prior week. No chart — pure stats.
 */
export function WeeklySummary({ data, today }: Props) {
  const stats = useMemo(() => {
    const thisWeekStart = addDays(today, -6);
    const lastWeekEnd = addDays(thisWeekStart, -1);
    const lastWeekStart = addDays(lastWeekEnd, -6);

    let total = 0;
    let priorTotal = 0;
    const byDay = new Map<string, number>();
    const byCat = new Map<string, number>();

    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      const thisWeek = txContributionInRange(t, thisWeekStart, today);
      if (thisWeek > 0) {
        total += thisWeek;
        byDay.set(t.date, (byDay.get(t.date) ?? 0) + thisWeek);
        byCat.set(t.categoryId, (byCat.get(t.categoryId) ?? 0) + thisWeek);
      }
      priorTotal += txContributionInRange(t, lastWeekStart, lastWeekEnd);
    }

    const dailyAvg = total / 7;
    let biggestDay: { iso: string; amount: number } | null = null;
    for (const [iso, amount] of byDay.entries()) {
      if (!biggestDay || amount > biggestDay.amount) biggestDay = { iso, amount };
    }
    let topCat: { id: string; amount: number } | null = null;
    for (const [id, amount] of byCat.entries()) {
      if (!topCat || amount > topCat.amount) topCat = { id, amount };
    }
    const topCatName = topCat
      ? data.categories.find((c) => c.id === topCat!.id)?.name ?? '—'
      : '—';
    const change =
      priorTotal > 0 ? ((total - priorTotal) / priorTotal) * 100 : null;
    const biggestDayLabel = biggestDay ? dowName(biggestDay.iso) : '—';
    return {
      total,
      dailyAvg,
      biggestDayLabel,
      biggestDayAmount: biggestDay?.amount ?? 0,
      topCatName,
      topCatAmount: topCat?.amount ?? 0,
      change,
    };
  }, [data, today]);

  const currency = data.profile.currency;

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
        This week
      </h2>
      <div className="grid grid-cols-2 gap-4">
        <Stat
          label="Total spent"
          value={formatCurrency(stats.total, { currency })}
        />
        <Stat
          label="Daily average"
          value={formatCurrency(stats.dailyAvg, { currency })}
        />
        <Stat
          label="Biggest day"
          value={`${stats.biggestDayLabel ?? '—'} · ${formatCurrency(
            stats.biggestDayAmount,
            { currency },
          )}`}
        />
        <Stat label="Top category" value={stats.topCatName} />
      </div>
      {stats.change !== null ? (
        <p
          className={`mt-4 text-[13px] font-medium ${
            stats.change > 0 ? 'text-negative' : 'text-positive'
          }`}
        >
          {stats.change > 0 ? '+' : ''}
          {stats.change.toFixed(0)}% vs last week
        </p>
      ) : null}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 text-[15px] font-mono tabular-nums text-text-primary truncate">
        {value}
      </div>
    </div>
  );
}
